import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';

// Copy-Icon (clipboard)
function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}

// Check-Icon (success)
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// Einzelne Zitat-Card mit CSS-basiertem Expand/Collapse
const QuoteCard = React.memo(function QuoteCard({ quote, isActive, isFocused, onSelect, onCopy, copiedId, kbAction, onKbActionHandled }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);
  const cardRef = useRef(null);
  const uid = `${quote.zettelId}-${quote.text.slice(0, 20)}`;

  // Overflow-Erkennung: scrollHeight > clientHeight wenn line-clamp aktiv
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => {
      if (textRef.current) setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight + 2);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [quote.text]);

  // Keyboard-Aktionen — nur auf/zuklappen wenn es etwas aufzuklappen gibt
  useEffect(() => {
    if (!kbAction || !isFocused) return;
    if (kbAction === 'open' && isOverflowing) setExpanded(true);
    else if (kbAction === 'close') setExpanded(false);
    if (onKbActionHandled) onKbActionHandled();
  }, [kbAction]); // eslint-disable-line

  // Click-Outside: Karte zusammenklappen
  useEffect(() => {
    if (!expanded) return;
    const handlePointerDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [expanded]);

  return (
    <div
      ref={cardRef}
      className={`quote-card ${isActive ? 'quote-card-active' : ''} ${isFocused ? 'quote-card-focused' : ''}`}
      onClick={(e) => {
        if (e.target.closest('.hl-expand-btn') || e.target.closest('.quote-card-copy')) return;
        onSelect(quote);
      }}
    >
      {/* Zitat-Text: CSS line-clamp wenn collapsed, volle Höhe wenn expanded */}
      <div ref={textRef} className={`quote-card-text ${expanded ? '' : 'quote-card-text-clamped'}`}>
        &bdquo;{quote.text}&ldquo;
        {!expanded && isOverflowing && (
          <div className="quote-card-gradient" />
        )}
      </div>
      {quote.author && (
        <div className="quote-card-author">— {quote.author}</div>
      )}
      {(isOverflowing || expanded) && (
        <button
          className={`hl-expand-btn${expanded ? ' is-expanded' : ''}`}
          onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}
        >
          {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
        </button>
      )}
      <div className="quote-card-footer">
        <span className="quote-card-source">{quote.zettelId}</span>
        <span
          className={`quote-card-copy ${copiedId === uid ? 'copied' : ''}`}
          role="button"
          tabIndex={-1}
          title="Zitat kopieren"
          onClick={(e) => onCopy(e, quote)}
        >
          {copiedId === uid ? <CheckIcon /> : <CopyIcon />}
        </span>
      </div>
    </div>
  );
});

export default function QuoteView({ quotes = [], onSelectZettel, visible, selectedId }) {
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [kbAction, setKbAction] = useState(null);
  const gridRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return quotes;
    const q = search.toLowerCase();
    return quotes.filter(quote =>
      quote.text.toLowerCase().includes(q) ||
      (quote.author && quote.author.toLowerCase().includes(q))
    );
  }, [quotes, search]);

  useEffect(() => { setFocusedIndex(0); }, [filtered.length]);

  useEffect(() => {
    if (!gridRef.current) return;
    const cards = gridRef.current.querySelectorAll('.quote-card');
    if (cards[focusedIndex]) {
      cards[focusedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex]);

  const handleCopy = useCallback((e, quote) => {
    e.stopPropagation();
    const text = quote.author
      ? `\u201E${quote.text}\u201C \u2014 ${quote.author}`
      : `\u201E${quote.text}\u201C`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(`${quote.zettelId}-${quote.text.slice(0, 20)}`);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => { /* intentional */ });
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (filtered.length === 0) return;
    if (document.activeElement === searchRef.current) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        searchRef.current.blur();
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % filtered.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
        break;
      case 'ArrowRight':
        e.preventDefault();
        setKbAction('open');
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setKbAction('close');
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[focusedIndex]) {
          onSelectZettel(filtered[focusedIndex].zettelId, filtered[focusedIndex].text);
        }
        break;
      default:
        break;
    }
  }, [filtered, focusedIndex, onSelectZettel]);

  if (!visible) return null;

  return (
    <div className="quote-view" tabIndex={0} onKeyDown={handleKeyDown}>
      <div className="quote-search-bar">
        <input
          ref={searchRef}
          className="quote-search-input"
          type="text"
          placeholder="Zitate durchsuchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      {filtered.length === 0 ? (
        <div className="quote-empty">
          <div className="quote-empty-icon">&#10078;</div>
          <p className="quote-empty-text">
            {search ? 'Keine Zitate gefunden.' : 'Noch keine Zitate gesammelt.'}
          </p>
          {!search && (
            <p className="quote-empty-hint">
              Formatiere Text in einem Zettel als Blockquote mit Autorenzeile — er erscheint dann hier.
            </p>
          )}
        </div>
      ) : (
        <div className="quote-grid" ref={gridRef}>
          {filtered.map((quote, i) => (
            <QuoteCard
              key={`${quote.zettelId}-${i}`}
              quote={quote}
              isActive={quote.zettelId === selectedId}
              isFocused={i === focusedIndex}
              kbAction={i === focusedIndex ? kbAction : null}
              onKbActionHandled={() => setKbAction(null)}
              onSelect={(q) => {
                setFocusedIndex(i);
                onSelectZettel(q.zettelId, q.text);
              }}
              onCopy={handleCopy}
              copiedId={copiedId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
